<template>
	<app-title-bar :icon="['fas', 'fa-truck']" subtitle="Journey" title="Journey List">
		<div class="flex flex-col-reverse sm:flex-row items-center gap-4">
			<app-button to="/hire-desk" icon="fa-circle-left"> Back </app-button>
			<depots-dropdown
				v-if="depots"
				v-model="depots"
				update-user
				@update:model-value="getData(true)" />
		</div>
	</app-title-bar>

	<div class="xs:flex gap-4">
		<app-panel class="grow" title="Journeys">
			<template #header>
				<div class="flex flex-col lg:flex-row items-center gap-4">
					<div class="w-[320px] flex-shrink-0">
						<vehicles-dropdown
							v-model="params.vehicleReg"
							:disabled="loading"
							:label-key="['code', 'name']"
							value-key="registration"
							@update:model-value="getData" />
					</div>

					<div class="w-[260px] flex-shrink-0">
						<vue-date-picker
							v-model="params.date"
							class="mabey-date-picker"
							:enable-time-picker="false"
							model-type="yyyy-MM-dd"
							format="dd/MM/yyyy"
							auto-apply
							:disabled="loading"
							:min-date="new Date()"
							@update:model-value="getData" />
					</div>
					<app-button
						class="shrink-0"
						size="small"
						:disabled="loading"
						@click="clearFilter"
						>Clear Filters
					</app-button>
				</div>
			</template>
			<datatable
				:headers
				:value="items"
				container-class="min-h-[500px]"
				:loading
				clickable
				@row-click="handleRowClick">
				<template #row-status="{ row }">
					<div class="w-7 h-10 inline-flex items-center justify-center">
						<app-tooltip
							:text="getStatusIcon(row, 'journey_status').label"
							position-x="left"
							modifiers="left-[38px]"
							small>
							<app-svg
								:path="`/icons/catalyst/${
									getStatusIcon(row, 'journey_status').icon
								}.svg`"
								:colour="getStatusIcon(row, 'journey_status').colour"
								autoscale />
						</app-tooltip>
					</div>
				</template>

				<template #row-vehicle_id="{ row }">
					{{ row.delivery_method }}
				</template>

				<template #row-step_number="{ row }">
					<app-dropdown
						v-model="row.step_number"
						boundary-element=".p-datatable"
						:disabled="!params.vehicleReg || !params.date"
						label-key="label"
						value-key="value"
						class="!min-w-0 w-[100px]"
						size="small"
						:options="steps"
						@click.stop
						@update:model-value="(val: number) => updateStepNumber(row, val)"
						>Step No</app-dropdown
					>
				</template>
			</datatable>

			<div v-if="items.length > 0" class="flex justify-end mt-4">
				<app-button class="mr-3" :disabled="!itemsToSend" @click="save()">
					Save
				</app-button>
				<app-button :disabled="!itemsToSend" @click="save(true)">
					Send To Driver
				</app-button>
			</div>
		</app-panel>
	</div>
</template>

<script lang="ts" setup>
	import DepotsDropdown from '@/components/partials/dropdowns/DepotsDropdown.vue'
	import { ref, computed, onMounted } from 'vue'
	import type { Header, Row } from '@/data/types/datatable'
	import AppSvg from '@/components/base/AppSvg.vue'
	import useAuthStore from '@/stores/auth'
	import useOperatorStore from '@/stores/operators'
	import { useRouter } from 'vue-router'
	import useToasterStore from '@/stores/toaster'
	import { getStatusIcon } from '@/composables/status'
	import JourneyService from '@/services/journey'
	import ContractService from '@/services/contract'
	import type { Journey } from '@/data/schema/journey'
	import { formatDate } from '@/utils/date'
	import VehiclesDropdown from '@/components/partials/dropdowns/VehiclesDropdown.vue'
	import VueDatePicker from '@vuepic/vue-datepicker'
	import '@vuepic/vue-datepicker/dist/main.css'

	// Resources
	const journeys = new JourneyService()
	const contracts = new ContractService()
	const toaster = useToasterStore()
	const router = useRouter()
	const auth = useAuthStore()

	const operators = useOperatorStore()

	// Data
	const depots = ref<string | null>(null)
	const items = ref<Journey[]>([])
	const loading = ref(true)
	const params = ref({ vehicleReg: '', date: undefined })
	const steps = Array.from({ length: 20 }, (v, i) => ({ label: `${i + 1}`, value: i + 1 }))
	// const date = ref(new Date());
	// In case of a range picker, you'll receive [Date, Date]

	const headers: Header[] = [
		{ value: 'status', text: 'Status' },
		{ value: 'journey_date', text: 'Jrn Date' },
		{ value: 'vehicle_id', text: 'Vehicle ID' },
		{ value: 'type', text: 'Type' },
		{ value: 'journey_number', text: 'Jrn No' },
		{ value: 'link_number', text: 'Link No' },
		{ value: 'account_name', text: 'Account Name' },
		{ value: 'account_number', text: 'Account No' },
		{ value: 'site_contact_name', text: 'Site Name' },
		{ value: 'delivery_address', text: 'Site Address' },
		{ value: 'depot', text: 'Site' },
		{ value: 'weight', text: 'Weight' },
		{ value: 'step_number', text: 'Step No' }
	]

	const itemsToSend = computed(() => {
		return items.value.some((item) => item.step_number)
	})

	function handleRowClick(item: Row) {
		if (item && item.id) {
			router.push(`/hire-desk/journey-item/${item.id}`)
		}
	}

	const getData = (updateOperators = false) => {
		loading.value = true

		if (updateOperators) {
			operators.set(auth!.user!.current_depot!)
		}

		if (depots.value) {
			contracts
				.getHireDeskArea('journey', depots.value!, 1, 1000, null, {}, params.value)
				.then((res) => {
					items.value = res.data.data.map((obj: Journey) => ({
						...obj,
						journey_date: formatDate(obj.journey_date)
					}))

					loading.value = false
				})
				.catch(() => {
				
					toaster.error({ text: 'An error occurred retrieving journeys' })
					loading.value = false
				})
		} else {
			toaster.error({ text: 'Invalid depot' })
		}
	}

	function clearFilter() {
		params.value.vehicleReg = ''
		params.value.date = undefined
		getData()
	}

	function updateStepNumber(item: Journey, value: number | null) {
		toaster.clear()
		const index = items.value.findIndex((i) => i.id === item.id)

		if (!value) {
			items.value[index].step_number = null
			return
		} else {
			const isDuplicate = items.value.some(
				(i) =>
					i.vehicle_registration === item.vehicle_registration &&
					i.journey_date === item.journey_date &&
					i.step_number === value
			)

			if (isDuplicate) {
				toaster.error({ text: 'Duplicate step number for the same vehicle and date' })
				return
			}

			if (index !== -1) {
				items.value[index].step_number = value
			}
		}
	}

	function save(sendToDriver = false) {
		const itemsToSend = items.value
			.filter((item) => item.step_number)
			.map((item) => {
				return sendToDriver
					? { id: item.id, step_number: item.step_number, driver_status: '1' }
					: { id: item.id, step_number: item.step_number }
			})

		toaster.working({
			text: 'Saving journeys...',
			dismissable: false,
			timeout: 0
		})

		journeys
			.updateItems(itemsToSend)
			.then(() => {
				toaster.clear()
				toaster.success({
					text: 'Changes saved successfully'
				})
				getData()
			})
			.catch((error) => {
				console.error(error)
			})
	}

	onMounted(() => {
		if (auth.user && auth.user.current_depot) {
			depots.value = auth.user.current_depot
		}

		getData(true)
	})
</script>
